/**
 * Contract Card Component
 * Migrated to use cambrian-ds Card, Button, and design tokens
 */

import { Card, CardBody, Chip, Button } from "cambrian-ds";
import { useState } from 'react';
import type { ProjectContract } from '../types/project';
import { cn } from "@/lib/utils";
import { cambrianColors, typography, spacing } from "@/styles/design-tokens";

interface ContractCardProps {
  contract: ProjectContract;
  chain: string;
}

export function ContractCard({ contract, chain }: ContractCardProps) {
  const [copied, setCopied] = useState(false);

  const copyAddress = async () => {
    await navigator.clipboard.writeText(contract.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncateAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const chainColors: Record<string, string> = {
    ethereum: 'bg-blue-100 text-blue-800',
    base: 'bg-indigo-100 text-indigo-800',
    polygon: 'bg-purple-100 text-purple-800',
    arbitrum: 'bg-cyan-100 text-cyan-800',
    solana: 'bg-green-100 text-green-800',
    bnb: 'bg-yellow-100 text-yellow-800',
    optimism: 'bg-red-100 text-red-800',
  };

  return (
    <Card className={cn(
      cambrianColors.bg.surface,
      cambrianColors.border.primary,
      "hover:shadow-elevation-medium transition-all"
    )}>
      <CardBody className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          {/* Contract name/symbol */}
          <div className="flex items-center gap-2 mb-1">
            {contract.isPrimaryContract && (
              <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            )}
            <h4 className={cn(typography.h5, cambrianColors.text.heading)}>
              {contract.name || 'Unknown Contract'}
              {contract.symbol && <span className={cn("ml-2", cambrianColors.text.muted)}>({contract.symbol})</span>}
            </h4>
          </div>

          {/* Address with copy button */}
          <div className="flex items-center gap-2 mb-2">
            <code className={cn(
              typography.codeInline,
              cambrianColors.text.secondary,
              cambrianColors.bg.code,
              "px-2 py-1 rounded"
            )}>
              {truncateAddress(contract.address)}
            </code>
            <Button
              onPress={copyAddress}
              size="sm"
              variant="tertiary"
              isIconOnly
              className={cn(cambrianColors.text.tertiary, "hover:text-cambrian-brand transition-colors")}
              title="Copy address"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </Button>
          </div>
        </div>

        {/* Chain badge */}
        <Chip
          variant="flat"
          size="sm"
          className={chainColors[chain] || 'bg-gray-100 text-gray-800'}
        >
          {chain}
        </Chip>
      </div>

      {/* Metadata */}
      <div className={cn("flex flex-wrap mt-3", spacing.sm, typography.small, cambrianColors.text.muted)}>
        {contract.repoCount !== undefined && contract.repoCount > 0 && (
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            {contract.repoCount} {contract.repoCount === 1 ? 'repo' : 'repos'}
          </span>
        )}
        {contract.websiteUrl && (
          <a
            href={contract.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-1",
              cambrianColors.brand.primary,
              "hover:text-cambrian-brand-hover dark:hover:text-green-500"
            )}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            Website
          </a>
        )}
        {contract.twitterUrl && (
          <a
            href={contract.twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-1",
              cambrianColors.brand.primary,
              "hover:text-cambrian-brand-hover dark:hover:text-green-500"
            )}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
            </svg>
            Twitter
          </a>
        )}
      </div>
      </CardBody>
    </Card>
  );
}
